import type { QueryFunctionContext } from '@tanstack/react-query';

/**
 * Wraps a query function to treat 404 responses as valid "null" states.
 *
 * This is useful for optional resources that may not exist yet (e.g., AccountProfile,
 * BrandingSettings, TelephonyConfiguration), where a 404 means "resource not created"
 * rather than an error.
 *
 * The wrapper forwards the QueryFunctionContext (including abort signal) to support
 * query cancellation. Callers can ignore the context parameter if not needed.
 *
 * @example
 * ```typescript
 * export function useAccountProfileQuery(params: { impersonationContext?: Api.IImpersonationContext }) {
 *   const userSession = useUserSession();
 *   return useQuery<Api.AccountProfile | null>({
 *     queryKey: ['accountProfile', params.impersonationContext],
 *     queryFn: handle404AsNull(async () => {
 *       return userSession.webServiceHelper.callAsync<Api.AccountProfile>(
 *         Api.ImpersonationBroker.composeApiUrl({
 *           urlPath: 'accountProfile',
 *           impersonationContext: params.impersonationContext,
 *         }),
 *         'GET'
 *       );
 *     }),
 *   });
 * }
 * ```
 *
 * @example Using abort signal for cancellation:
 * ```typescript
 * queryFn: handle404AsNull(async ({ signal }) => {
 *   return fetch('/api/data', { signal });
 * })
 * ```
 *
 * @param queryFn - The async function that fetches data (can optionally use QueryFunctionContext)
 * @returns A wrapped function that returns T | null
 */
export function handle404AsNull<T>(
	queryFn: (context?: QueryFunctionContext) => Promise<T>
): (context?: QueryFunctionContext) => Promise<T | null> {
	return async context => {
		try {
			return await queryFn(context);
		} catch (error) {
			// Treat 404 as valid "resource doesn't exist" state
			if (typeof error === 'object' && error != null && 'systemCode' in error && error.systemCode === 404) {
				return null;
			}
			throw error;
		}
	};
}
